import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Circle,
  Database,
  Calendar
} from 'lucide-react';
import { versionsApi, autEndpointsApi } from '../services/api';
import { AUTVersion, AUTEndpoint } from '../types';

interface VersionsProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const Versions: React.FC<VersionsProps> = ({ addNotification }) => {
  const [versions, setVersions] = useState<AUTVersion[]>([]);
  const [endpoints, setEndpoints] = useState<AUTEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingVersion, setEditingVersion] = useState<AUTVersion | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [formData, setFormData] = useState({
    version_name: '',
    description: '',
    is_active: false,
    aut_endpoint_id: 0
  });

  const [editFormData, setEditFormData] = useState({
    version_name: '',
    description: '',
    aut_endpoint_id: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      if (!orgId || !projectId) {
        setVersions([]);
        setEndpoints([]);
        setLoading(false);
        return;
      }
      const [versionsData, endpointsData] = await Promise.all([
        versionsApi.list(orgId, projectId),
        autEndpointsApi.list(orgId, projectId)
      ]);
      setVersions(versionsData);
      setEndpoints(endpointsData);
    } catch (error) {
      addNotification('Failed to load data', 'error');
      console.error('Error loading versions/endpoints:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = () => loadData();
  const endpointName = (id: number) => endpoints.find((e) => e.id === id)?.name ?? `Endpoint #${id}`;

  const handleCreateVersion = async () => {
    try {
      if (!formData.version_name.trim()) {
        addNotification('Version name is required', 'error');
        return;
      }
      if (!formData.aut_endpoint_id) {
        addNotification('Please select an endpoint', 'error');
        return;
      }
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      if (!orgId || !projectId) {
        addNotification('Please select an organization and project first', 'error');
        return;
      }
      await versionsApi.create(orgId, projectId, {
        version_name: formData.version_name,
        description: formData.description,
        is_active: formData.is_active,
        aut_endpoint_id: formData.aut_endpoint_id
      });
      addNotification('Version created successfully', 'success');
      setShowCreateModal(false);
      setFormData({ version_name: '', description: '', is_active: false, aut_endpoint_id: endpoints[0]?.id ?? 0 });
      loadVersions();
    } catch (error) {
      addNotification('Failed to create version', 'error');
      console.error('Error creating version:', error);
    }
  };

  const handleActivateVersion = async (id: number) => {
    try {
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      if (!orgId || !projectId) {
        addNotification('Please select an organization and project first', 'error');
        return;
      }
      await versionsApi.activate(orgId, projectId, id);
      addNotification('Version activated successfully', 'success');
      loadVersions();
    } catch (error) {
      addNotification('Failed to activate version', 'error');
      console.error('Error activating version:', error);
    }
  };

  const handleDeleteVersion = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this version? This action cannot be undone.')) {
      return;
    }

    try {
      const orgIdStr = localStorage.getItem('selectedOrganizationId');
      const projectIdStr = localStorage.getItem('selectedProjectId');
      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
      if (!orgId || !projectId) {
        addNotification('Please select an organization and project first', 'error');
        return;
      }
      await versionsApi.delete(orgId, projectId, id);
      addNotification('Version deleted successfully', 'success');
      loadVersions();
    } catch (error) {
      addNotification('Failed to delete version', 'error');
      console.error('Error deleting version:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Versions</h1>
            <p className="text-slate-300 mt-2">Manage different versions of your Application Under Test</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Version
          </button>
        </div>
      </div>

      {/* Versions List */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="text-lg font-medium text-slate-100">Version List</h3>
        </div>
        
        {versions.length === 0 ? (
          <div className="text-center py-12">
            <Database className="h-12 w-12 mx-auto mb-3 text-slate-500" />
            <p className="text-slate-400 text-lg">No versions created yet</p>
            <p className="text-slate-400 text-sm">Create your first version to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Version
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-700">
              <thead className="bg-slate-700/50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Version Name</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Endpoint</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Description</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Created</th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-slate-800 divide-y divide-slate-700">
                {versions.map((version) => (
                  <tr key={version.id} className="hover:bg-slate-700/30">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {version.is_active ? (
                          <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                        ) : (
                          <Circle className="h-5 w-5 text-slate-500 mr-2" />
                        )}
                        <span className={`text-sm font-medium ${
                          version.is_active ? 'text-green-400' : 'text-slate-400'
                        }`}>
                          {version.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-100">{version.version_name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">{endpointName(version.aut_endpoint_id)}</td>
                    <td className="px-6 py-4 text-sm text-slate-300 max-w-xs truncate" title={version.description || ''}>
                      {version.description || <span className="text-slate-500">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-400">
                      <span className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {formatDate(version.created_at)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end space-x-2">
                        {!version.is_active && (
                          <button
                            onClick={() => handleActivateVersion(version.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-green-200 bg-green-900/50 hover:bg-green-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() => {
setEditingVersion(version);
                        setEditFormData({
                          version_name: version.version_name,
                          description: version.description || '',
                          aut_endpoint_id: version.aut_endpoint_id
                        });
                        setShowEditModal(true);
                          }}
                          className="inline-flex items-center px-3 py-1 border border-slate-600 text-xs font-medium rounded text-slate-200 bg-slate-800 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        {!version.is_active && (
                          <button
                            onClick={() => handleDeleteVersion(version.id)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-200 bg-red-900/50 hover:bg-red-800/50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Version Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Create New Version</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Endpoint *
                  </label>
                  <select
                    value={formData.aut_endpoint_id || ''}
                    onChange={(e) => setFormData({ ...formData, aut_endpoint_id: parseInt(e.target.value, 10) })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Select endpoint</option>
                    {endpoints.map((ep) => (
                      <option key={ep.id} value={ep.id}>{ep.name}</option>
                    ))}
                  </select>
                  {endpoints.length === 0 && (
                    <p className="text-xs text-amber-400 mt-1">Create an endpoint in Endpoints first.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Version Name *
                  </label>
                  <input
                    type="text"
                    value={formData.version_name}
                    onChange={(e) => setFormData({ ...formData, version_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="e.g., v1.0.0, Production-2024"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-indigo-500"
                    placeholder="Optional description of this version..."
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateVersion}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Create Version
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Version Modal */}
      {showEditModal && editingVersion && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-slate-800 border border-slate-700">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-slate-100 mb-4">Edit Version</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Endpoint *
                  </label>
                  <select
                    value={editFormData.aut_endpoint_id || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, aut_endpoint_id: parseInt(e.target.value, 10) })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Select endpoint</option>
                    {endpoints.map((ep) => (
                      <option key={ep.id} value={ep.id}>{ep.name}</option>
                    ))}
                  </select>
                  {endpoints.length === 0 && (
                    <p className="text-xs text-amber-400 mt-1">No endpoints. Create one in Endpoints first.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Version Name *
                  </label>
                  <input
                    type="text"
                    value={editFormData.version_name}
                    onChange={(e) => setEditFormData({ ...editFormData, version_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="e.g., v1.0.1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-600 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-purple-500"
                    placeholder="Optional description..."
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => { setShowEditModal(false); setEditingVersion(null); }}
                  className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    try {
                      if (!editFormData.version_name.trim()) {
                        addNotification('Version name is required', 'error');
                        return;
                      }
                      const orgIdStr = localStorage.getItem('selectedOrganizationId');
                      const projectIdStr = localStorage.getItem('selectedProjectId');
                      const orgId = orgIdStr ? parseInt(orgIdStr) : undefined;
                      const projectId = projectIdStr ? parseInt(projectIdStr) : undefined;
                      if (!orgId || !projectId || !editingVersion) {
                        addNotification('Please select an organization and project first', 'error');
                        return;
                      }
                      if (!editFormData.aut_endpoint_id) {
                        addNotification('Please select an endpoint', 'error');
                        return;
                      }
                      await versionsApi.update(orgId, projectId, editingVersion.id, {
                        version_name: editFormData.version_name,
                        description: editFormData.description,
                        aut_endpoint_id: editFormData.aut_endpoint_id
                      });
                      addNotification('Version updated successfully', 'success');
                      setShowEditModal(false);
                      setEditingVersion(null);
                      loadVersions();
                    } catch (error) {
                      addNotification('Failed to update version', 'error');
                      console.error('Error updating version:', error);
                    }
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Versions;
